
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * MarginCallNotify struct
 */
class MarginCallNotify {
  /**
   * Initialize struct
   * @param {!Date=} timestamp
   * @param {!number=} marginLevel
   * @constructor
   */
  constructor (timestamp = new Date(0), marginLevel = 0.0) {
    this.Timestamp = timestamp
    this.MarginLevel = marginLevel
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarginCallNotify}
   * @param {!MarginCallNotify} other Other struct
   * @returns {!MarginCallNotify} This struct
   */
  copy (other) {
    if (other.Timestamp != null) {
      if (other.Timestamp instanceof Date) {
        this.Timestamp = new Date(other.Timestamp.getTime())
      } else {
        this.Timestamp = new Date(Math.round(other.Timestamp / 1000000))
      }
    } else {
      this.Timestamp = null
    }
    if (other.MarginLevel != null) {
      this.MarginLevel = other.MarginLevel
    } else {
      this.MarginLevel = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarginCallNotify}
   * @returns {!MarginCallNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarginCallNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarginCallNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarginCallNotify}
   * @param {!MarginCallNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarginCallNotify)) {
      throw new TypeError('Instance of MarginCallNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarginCallNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Timestamp: ((this.Timestamp != null) ? (this.Timestamp.getTime() * 1000000) : null),
      MarginLevel: ((this.MarginLevel != null) ? this.MarginLevel : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarginCallNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarginCallNotify} other Object value
   * @returns {!MarginCallNotify} Created struct
   */
  static fromObject (other) {
    return new MarginCallNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarginCallNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarginCallNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarginCallNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5075
  }
}

export { MarginCallNotify };

/**
 * Fast Binary Encoding MarginCallNotify field model
 */
class FieldModelMarginCallNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Timestamp = new fbe.FieldModelTimestamp(buffer, 4 + 4)
    this.MarginLevel = new fbe.FieldModelDouble(buffer, this.Timestamp.fbeOffset + this.Timestamp.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelMarginCallNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarginCallNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Timestamp.fbeSize + this.MarginLevel.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarginCallNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Timestamp.fbeExtra + this.MarginLevel.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarginCallNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarginCallNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarginCallNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5075
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarginCallNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarginCallNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Timestamp.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Timestamp.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timestamp.fbeSize

    if ((fbeCurrentSize + this.MarginLevel.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarginLevel.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginLevel.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarginCallNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarginCallNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarginCallNotify}
   * @param {!MarginCallNotify} fbeValue Default value, defaults is new MarginCallNotify()
   * @returns {!MarginCallNotify} MarginCallNotify value
   */
  get (fbeValue = new MarginCallNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarginCallNotify}
   * @param {!MarginCallNotify} fbeValue MarginCallNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Timestamp.fbeSize) <= fbeStructSize) {
      fbeValue.Timestamp = this.Timestamp.get()
    } else {
      fbeValue.Timestamp = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timestamp.fbeSize

    if ((fbeCurrentSize + this.MarginLevel.fbeSize) <= fbeStructSize) {
      fbeValue.MarginLevel = this.MarginLevel.get()
    } else {
      fbeValue.MarginLevel = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginLevel.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarginCallNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarginCallNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarginCallNotify}
   * @param {!MarginCallNotify} fbeValue MarginCallNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarginCallNotify}
   * @param {MarginCallNotify} fbeValue MarginCallNotify value
   */
  setFields (fbeValue) {
    this.Timestamp.set(fbeValue.Timestamp)
    this.MarginLevel.set(fbeValue.MarginLevel)
  }
}

export { FieldModelMarginCallNotify };

/**
 * Fast Binary Encoding MarginCallNotify model
 */
class MarginCallNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarginCallNotify(this.buffer, 4)
  }

  /**
   * Get the MarginCallNotify model
   * @this {!MarginCallNotifyModel}
   * @returns {!FieldModelMarginCallNotify} model MarginCallNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarginCallNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarginCallNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarginCallNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarginCallNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarginCallNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarginCallNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarginCallNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarginCallNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarginCallNotifyModel}
   * @param {!MarginCallNotify} value MarginCallNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarginCallNotifyModel}
   * @param {!MarginCallNotify} value MarginCallNotify value, defaults is new MarginCallNotify()
   * @return {!object} Deserialized MarginCallNotify value and its size
   */
  deserialize (value = new MarginCallNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarginCallNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarginCallNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarginCallNotifyModel}
   * @param {!number} prev Previous MarginCallNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarginCallNotifyModel };
